import React from 'react';

import { useHistory } from 'react-router-dom';

import { UpdateUserRequest } from '@recurrency/core-api';
import { Form, message, PageHeader } from 'antd';

import { Button } from 'components/Button';
import { InputFormItem, defaultFormLayout, defaultFormTailLayout, SelectFormItem } from 'components/FormItems';
import { Container } from 'components/Layout/Container';
import { CenteredLoader, CenteredError } from 'components/Loaders';

import { useApi } from 'hooks/useApi';

import { api } from 'utils/api';
import { captureAndShowError } from 'utils/error';
import { routes, IdPathParams, usePathParams } from 'utils/routes';
import { getTimezoneOptions } from 'utils/timezones';

import { UNITS } from 'constants/styles';

const tzOptions = getTimezoneOptions();

export const UserEditPage = () => {
  const { id } = usePathParams<IdPathParams>();
  const { data: user, isLoading, error, reload } = useApi().users().getUserById(id);

  const history = useHistory();

  const [form] = Form.useForm();

  if (error) {
    return <CenteredError error={error} />;
  }

  if (isLoading || !user) {
    return <CenteredLoader />;
  }

  const onSubmit = async (data: UpdateUserRequest) => {
    try {
      await api().users().updateUser(user.id, data);
      message.success('User updated.');
      reload(); // Despite redirecting, this reload is important for if the user returns to this page
      history.push(routes.internal.userDetails(user.id));
    } catch (err) {
      captureAndShowError(err, `updating user`);
    }
  };

  return (
    <Form.Provider
      onFormFinish={(name, { values, forms }) => {
        if (name === 'contactForm') {
          const { taskForm } = forms;

          taskForm.setFieldsValue({
            contact: `${values.firstName} ${values.lastName}`,
          });
        }
      }}
    >
      <Container>
        <PageHeader title="Edit User" onBack={() => history.goBack()} />
        <div
          style={{
            borderLeft: 'rgb(76, 130, 232) 4px solid',
            marginBottom: UNITS.XL,
            paddingLeft: UNITS.XL,
            marginLeft: '12rem',
            marginRight: '16rem',
          }}
        >
          <Form {...defaultFormLayout} form={form} onFinish={onSubmit} onError={console.error} initialValues={user}>
            <InputFormItem
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please add a first name.' }]}
            />

            <InputFormItem
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please add a last name.' }]}
            />

            <InputFormItem label="Email" name="email" rules={[{ required: true, message: 'Please add an email.' }]} />

            <SelectFormItem
              isLoading={false}
              initialValue=""
              label="Timezone"
              name="timezone"
              options={tzOptions}
              setFieldsValue={form.setFieldsValue}
            />

            <Form.Item {...defaultFormTailLayout}>
              <Button id="updateUserButton" type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Container>
    </Form.Provider>
  );
};
